// Web Push subscription helpers.
// Asks for browser permission, registers a push subscription against the
// service worker, and stores the subscription in Supabase `push_subscriptions`.

import { supabase } from './supabase';

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const supportsWebPush = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const subscribeToWebPush = async (userId) => {
  if (!supportsWebPush()) throw new Error('Web Push not supported in this browser');
  const vapid = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!vapid) throw new Error('VAPID public key not configured');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Permission denied');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }
  const endpoint = sub.endpoint;
  const p256dh = arrayBufferToBase64(sub.getKey('p256dh'));
  const auth = arrayBufferToBase64(sub.getKey('auth'));
  // upsert by endpoint
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({ user_id: userId, endpoint, p256dh, auth }, { onConflict: 'endpoint' });
  if (error) throw error;
  return sub;
};

export const unsubscribeFromWebPush = async () => {
  if (!supportsWebPush()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
    await sub.unsubscribe();
  }
};

export const isWebPushSubscribed = async () => {
  if (!supportsWebPush()) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
};
